/**
 * Playground boot-startup telemetry.
 *
 * The playground boot spans several async phases (Firebase init, auth,
 * profile creation, game auto-creation — including a full page reload — and
 * server-config upload) with no other visibility into which phase is slow.
 * This module is a lightweight, dependency-free collector that records
 * ordered phase marks, persists them to sessionStorage so they survive the
 * deliberate `creating-game` page reload (flagged via `expectBootReload()`),
 * and flushes a `playground_boot` event to cloud-run
 * (`POST /v1/playground-telemetry`) on the terminal `ready` / `error` states.
 * A persisted buffer left behind WITHOUT the reload flag is an abandoned boot
 * (manual refresh / crash) and is emitted alongside the terminal flush as a
 * separate `outcome: 'abandoned'` event. Cloud-run forwards to Hermes →
 * Databricks.
 *
 * Hard guarantees:
 * - Fire-and-forget: flush NEVER throws and NEVER blocks the boot path.
 * - No-ops outside playground mode (the singleton is only constructed when
 *   `isPlaygroundEnabled()`), so shipped games are untouched.
 * - The earliest boot signal comes from the injected toolbar script, which
 *   runs in a separate JS context (compiled string, own module graph — it
 *   cannot share this singleton). It stamps `window.__RUNDOT_PLAYGROUND_BOOT_START__`
 *   and appends observed marks to `window.__RUNDOT_PLAYGROUND_BOOT_MARKS__`;
 *   this module adopts/merges those on build.
 */

import { getCloudRunUrl, getPlaygroundConfig, isPlaygroundEnabled } from '../config/playground'
import { SDK_VERSION } from '../version'

declare global {
  interface Window {
    /** performance.now() stamped by the injected toolbar script at eval. */
    __RUNDOT_PLAYGROUND_BOOT_START__?: number
    /** Ordered marks observed by the injected toolbar script. */
    __RUNDOT_PLAYGROUND_BOOT_MARKS__?: Array<{ phase: string; at: number }>
  }
}

// ─── Known phases ───────────────────────────────────────────────────────────

/**
 * Informational list of phase names the SDK itself records. NOT an enforced
 * allowlist — `mark()` tolerates unknown names by design (e.g.
 * forward-compatible toolbar marks) so older SDKs can still merge marks
 * emitted by newer toolbar scripts.
 */
export const BOOT_PHASES = [
  'toolbar-script-start',
  'toolbar-script-start:post-reload',
  'loading-overlay-shown',
  'loading-overlay-shown:post-reload',
  'sdk-init',
  'state:initializing',
  'state:awaiting-sign-in',
  'state:creating-profile',
  'state:creating-game',
  'state:loading-game',
  'state:ready',
  'state:error',
  'firebase-init',
  'firebase-session-restore',
  'firebase-session-restore:timeout',
  'firebase-headless-token',
  'firebase-headless-signin',
  'firebase-custom-token-exchange',
  'firebase-tab-local-signin',
  'firebase-google-signin',
  'firebase-google-signin:redirect',
  'profile-ensure',
  'profile-fetch',
  'profile-fetch:error',
  'game-create',
  'game-create:reload',
  'game-create:error',
  'wait-for-profile',
  'server-config-upload',
  'server-config-upload:skipped',
  'server-config-upload:failed',
  'game-ready',
] as const

export type BootPhase = (typeof BOOT_PHASES)[number]

export type BootAuthPath = 'headless' | 'interactive' | 'unknown'

export type BootOutcome = 'ready' | 'error' | 'abandoned'

// ─── Event shape (must match the cloud-run zod schema) ─────────────────────

export interface BootPhaseEntry {
  name: string
  offsetMs: number
  durationMs?: number
}

export interface PlaygroundBootEvent {
  eventType: 'playground_boot'
  bootId: string
  /** Epoch ms when the boot started (survives the creating-game reload). */
  startedAt: number
  totalMs: number
  outcome: BootOutcome
  errorMessage?: string
  phases: BootPhaseEntry[]
  meta: {
    authPath: BootAuthPath
    slot: number | null
    reloaded: boolean
    previousAttemptPhases?: string[]
    hadGameId: boolean
    /** Playground target ('local' | 'dev' | 'staging' | 'playground'). */
    target: string
    sdkVersion: string
    /** Capped at 200 chars. */
    userAgent: string
    /** URL host only (no query string). */
    host: string
  }
}

// ─── Internal buffer ────────────────────────────────────────────────────────

const STORAGE_KEY = 'rundot.playground.boot'
const MAX_PHASES = 50
const MAX_ERROR_MESSAGE_LENGTH = 300
const MAX_USER_AGENT_LENGTH = 200
/** Prior-boot buffers carried for re-emission; oldest dropped beyond this. */
const MAX_HELD_ATTEMPTS = 3
/** Matches the cloud-run zod cap — events beyond 1h are clamped, not rejected. */
export const MAX_TOTAL_MS = 3_600_000

interface StoredMark {
  phase: string
  /** Epoch ms at mark time — reload-safe (performance.now resets per page). */
  epoch: number
  durationMs?: number
}

interface BootBuffer {
  bootId: string
  startedAtEpoch: number
  marks: StoredMark[]
  authPath: BootAuthPath
  slot: number | null
  hadGameId: boolean
  /** Set only immediately before the deliberate game-create page reload. */
  expectingReload?: boolean
  /** Target/sdkVersion captured at buffer creation — absent in pre-upgrade buffers. */
  target?: string
  sdkVersion?: string
  /** Set when this buffer itself resulted from a merge, so a later abandoned emission keeps the reload meta. */
  reloaded?: boolean
  previousAttemptPhases?: string[]
  /**
   * Terminal outcome recorded just before delivery. If delivery failed, the
   * next boot re-emits this buffer with THIS outcome instead of mislabeling
   * the finished boot as abandoned.
   */
  terminalOutcome?: 'ready' | 'error'
  terminalErrorMessage?: string
  /**
   * Prior-boot buffers held for re-emission (abandoned or finished-but-
   * undelivered). Persisted WITH the buffer so they survive the deliberate
   * game-create/OAuth reloads — in-memory-only holding lost them on reload.
   * Nested heldAttempts are flattened into this list, never nested.
   */
  heldAttempts?: BootBuffer[]
}

export interface BootTelemetryDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  nowEpoch?: () => number
  randomId?: () => string
  /** Injectable transport; defaults to the authenticated cloud-run POST. */
  postEvent?: (event: PlaygroundBootEvent) => Promise<void>
  sdkVersion?: string
  userAgent?: string
  host?: string
  hadGameId?: boolean
  /** Injectable navigation-start clock; defaults to performance.timeOrigin. */
  navStartEpoch?: () => number | null
  /** Injectable playground target; defaults to getPlaygroundConfig()?.target. */
  target?: string
  warn?: (message: string) => void
}

function defaultNowEpoch(): number {
  return Date.now()
}

function defaultRandomId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // fall through to Math.random-based id
  }
  return `boot-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }
  } catch {
    // storage unavailable (privacy mode etc.)
  }
  return null
}

function defaultNavStartEpoch(): number | null {
  try {
    if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
      return Math.round(performance.timeOrigin)
    }
  } catch {
    // performance unavailable
  }
  return null
}

function cap(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

/** Validate a persisted buffer (top-level or held attempt); null when unusable. */
function parseStoredBuffer(parsed: Partial<BootBuffer>): BootBuffer | null {
  if (
    typeof parsed.bootId !== 'string' ||
    typeof parsed.startedAtEpoch !== 'number' ||
    !Array.isArray(parsed.marks)
  ) {
    return null
  }
  return {
    bootId: parsed.bootId,
    startedAtEpoch: parsed.startedAtEpoch,
    marks: parsed.marks.filter(
      (m): m is StoredMark =>
        !!m &&
        typeof m.phase === 'string' &&
        typeof m.epoch === 'number' &&
        (m.durationMs === undefined || typeof m.durationMs === 'number'),
    ),
    authPath:
      parsed.authPath === 'headless' || parsed.authPath === 'interactive'
        ? parsed.authPath
        : 'unknown',
    slot: typeof parsed.slot === 'number' ? parsed.slot : null,
    hadGameId: parsed.hadGameId === true,
    expectingReload: parsed.expectingReload === true,
    // Absent in buffers persisted by pre-upgrade SDKs — callers fall back
    // to this instance's values.
    target: typeof parsed.target === 'string' ? parsed.target : undefined,
    sdkVersion: typeof parsed.sdkVersion === 'string' ? parsed.sdkVersion : undefined,
    reloaded: parsed.reloaded === true ? true : undefined,
    previousAttemptPhases: Array.isArray(parsed.previousAttemptPhases)
      ? parsed.previousAttemptPhases.filter((p): p is string => typeof p === 'string')
      : undefined,
    terminalOutcome:
      parsed.terminalOutcome === 'ready' || parsed.terminalOutcome === 'error'
        ? parsed.terminalOutcome
        : undefined,
    terminalErrorMessage:
      typeof parsed.terminalErrorMessage === 'string'
        ? parsed.terminalErrorMessage
        : undefined,
  }
}

/** Convert a performance.now() reading to epoch ms using the page time origin. */
function performanceAtToEpoch(at: number): number | null {
  try {
    if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
      return Math.round(performance.timeOrigin + at)
    }
  } catch {
    // performance unavailable
  }
  return null
}

export class BootTelemetry {
  private buffer: BootBuffer
  private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
  private readonly nowEpoch: () => number
  private readonly postEvent: (event: PlaygroundBootEvent) => Promise<void>
  private readonly sdkVersion: string
  private readonly userAgent: string
  private readonly host: string
  private readonly target: string
  private readonly warn: (message: string) => void
  private reloaded = false
  private previousAttemptPhases: string[] | undefined
  private flushedOutcome: BootOutcome | null = null
  /** Toolbar marks already merged from THIS page's window globals. */
  private readonly mergedToolbarPhases = new Set<string>()
  /** Phase names carried over from a pre-reload buffer (empty for fresh boots). */
  private readonly preExistingPhases: Set<string>

  constructor(deps: BootTelemetryDeps = {}) {
    this.storage = deps.storage === undefined ? defaultStorage() : deps.storage
    this.nowEpoch = deps.nowEpoch ?? defaultNowEpoch
    this.postEvent = deps.postEvent ?? defaultPostEvent
    this.sdkVersion = deps.sdkVersion ?? defaultSdkVersion()
    this.userAgent = cap(
      deps.userAgent ??
        (typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
          ? navigator.userAgent
          : ''),
      MAX_USER_AGENT_LENGTH,
    )
    this.host = cap(
      deps.host ??
        (typeof window !== 'undefined' && window.location ? window.location.host : ''),
      128,
    )
    this.target = cap(deps.target ?? getPlaygroundConfig()?.target ?? 'unknown', 32)
    this.warn =
      deps.warn ??
      ((message: string) => {
        // eslint-disable-next-line no-console
        console.warn(message)
      })

    const randomId = deps.randomId ?? defaultRandomId
    const hadGameId =
      deps.hadGameId ?? (getPlaygroundConfig()?.gameId ? true : false)

    // Merge a persisted prior-attempt buffer ONLY when it was flagged for the
    // deliberate creating-game reload. sessionStorage survives any same-tab
    // reload, so an unflagged buffer is an abandoned boot (manual refresh /
    // crash), not a continuation — merging it would inflate totalMs.
    const prior = this.loadBuffer()
    if (prior?.expectingReload) {
      // Deliberate mid-boot reload (game-create) — continue the same boot.
      this.buffer = prior
      // Consume the flag: a LATER manual refresh of this same boot must fall
      // into the abandoned branch, not re-merge (and re-inflate totalMs).
      delete this.buffer.expectingReload
      this.reloaded = true
      this.previousAttemptPhases = prior.marks.map((m) => m.phase)
      // Persist the reload meta too: if THIS boot is later abandoned (manual
      // refresh), the abandoned event must keep reloaded/previousAttemptPhases.
      this.buffer.reloaded = true
      this.buffer.previousAttemptPhases = this.previousAttemptPhases
      this.persist()
    } else {
      this.buffer = {
        bootId: randomId(),
        startedAtEpoch: this.nowEpoch(),
        marks: [],
        authPath: 'unknown',
        slot: null,
        hadGameId,
        // Persist attribution so a LATER SDK version flushing this buffer
        // (e.g. as abandoned) reports the versions that actually ran the boot.
        target: this.target,
        sdkVersion: this.sdkVersion,
      }
      if (prior) {
        // Hold the prior boot for re-emission at this boot's flush: a buffer
        // with terminalOutcome FINISHED but its delivery failed (re-emit with
        // its recorded outcome, never as abandoned); anything else is an
        // abandoned boot (manual refresh / crash / closed tab). Flatten any
        // attempts the prior itself carried so chains of unfinished boots
        // don't lose them. Persisted on the buffer so the deliberate
        // game-create/OAuth reload carries them to the next page.
        const { heldAttempts: carried, ...priorAttempt } = prior
        const held = [...(carried ?? []), priorAttempt]
        this.buffer.heldAttempts = held.slice(-MAX_HELD_ATTEMPTS)
      }
    }

    // Fresh boots only: adopt the navigation start when it predates SDK init,
    // so the pre-toolbar segment (HTML fetch, Vite transforms, dep-optimization)
    // shows up as the offset of the first real phase. A merged buffer already
    // carries its original startedAtEpoch.
    if (!prior?.expectingReload) {
      const navStart = (deps.navStartEpoch ?? defaultNavStartEpoch)()
      if (navStart !== null && navStart < this.buffer.startedAtEpoch) {
        this.buffer.startedAtEpoch = navStart
      }
    }
    this.preExistingPhases = new Set(this.previousAttemptPhases ?? [])
  }

  /**
   * Record a phase mark. Unknown phase names are tolerated. When durationMs
   * is given, the mark's epoch is backdated by that amount so offsetMs still
   * points at the START of the measured operation. A buffer carrying a
   * terminalOutcome is a FINISHED boot: it stays immutable so a failed
   * delivery can be retried/re-emitted exactly as recorded (and late callers
   * like post-ready HMR uploads can't mutate it or drop its heldAttempts).
   */
  mark(phase: string, durationMs?: number): void {
    if (!phase || this.buffer.terminalOutcome) return
    this.mergeToolbarStamps()
    const duration = durationMs === undefined ? undefined : Math.max(0, durationMs)
    const epoch = duration === undefined ? this.nowEpoch() : this.nowEpoch() - duration
    const mark: StoredMark = { phase, epoch }
    if (duration !== undefined) mark.durationMs = duration
    this.buffer.marks.push(mark)
    this.persist()
  }

  /**
   * Time an async operation. Records the phase (with durationMs) on success,
   * records `${phase}:error` and rethrows on failure. The operation always
   * runs; on a FINISHED boot (terminalOutcome set) only the recording is
   * skipped — see mark().
   */
  async time<T>(phase: string, fn: () => Promise<T>): Promise<T> {
    this.mergeToolbarStamps()
    const start = this.nowEpoch()
    const record = (name: string): void => {
      if (this.buffer.terminalOutcome) return
      this.buffer.marks.push({
        phase: name,
        epoch: start,
        durationMs: Math.max(0, this.nowEpoch() - start),
      })
      this.persist()
    }
    try {
      const result = await fn()
      record(phase)
      return result
    } catch (error) {
      record(`${phase}:error`)
      throw error
    }
  }

  setAuthPath(authPath: BootAuthPath): void {
    this.buffer.authPath = authPath
    this.persist()
  }

  setSlot(slot: number | null): void {
    this.buffer.slot = slot
    this.persist()
  }

  /** Flag the buffer so the deliberate game-create reload merges (not abandons) it. */
  expectReload(): void {
    this.buffer.expectingReload = true
    this.persist()
  }

  /**
   * Undo expectReload() — call when the anticipated navigation never happened
   * (e.g. signInWithRedirect threw before navigating), so a later manual
   * refresh is treated as abandoned rather than merged.
   */
  cancelExpectReload(): void {
    delete this.buffer.expectingReload
    this.persist()
  }

  /** Build the wire payload. Merges any toolbar-stamped window marks first. */
  buildEvent(outcome: BootOutcome, errorMessage?: string): PlaygroundBootEvent {
    this.mergeToolbarStamps()
    return this.buildEventFrom(
      this.buffer,
      this.reloaded,
      this.previousAttemptPhases,
      outcome,
      this.nowEpoch(),
      errorMessage,
    )
  }

  /** Last recorded activity in a buffer; falls back to its start when markless. */
  private static lastActivityEpoch(buffer: BootBuffer): number {
    let last = buffer.startedAtEpoch
    for (const m of buffer.marks) {
      const end = m.epoch + (m.durationMs ?? 0)
      if (end > last) last = end
    }
    return last
  }

  private buildEventFrom(
    buffer: BootBuffer,
    reloaded: boolean,
    previousAttemptPhases: string[] | undefined,
    outcome: BootOutcome,
    endEpoch: number,
    errorMessage?: string,
  ): PlaygroundBootEvent {
    const sorted = [...buffer.marks].sort((a, b) => a.epoch - b.epoch)
    const startedAt = buffer.startedAtEpoch
    const phases: BootPhaseEntry[] = sorted.slice(0, MAX_PHASES).map((m) => {
      const entry: BootPhaseEntry = {
        name: cap(m.phase, 64),
        offsetMs: Math.max(0, m.epoch - startedAt),
      }
      if (m.durationMs !== undefined) entry.durationMs = m.durationMs
      return entry
    })

    const meta: PlaygroundBootEvent['meta'] = {
      authPath: buffer.authPath,
      slot: buffer.slot,
      reloaded,
      hadGameId: buffer.hadGameId,
      // Prefer the buffer's own attribution (captured when IT ran) — a buffer
      // persisted by an older/newer SDK must not report this instance's versions.
      target: buffer.target ?? this.target,
      sdkVersion: buffer.sdkVersion ?? this.sdkVersion,
      userAgent: this.userAgent,
      host: this.host,
    }
    if (previousAttemptPhases && previousAttemptPhases.length > 0) {
      meta.previousAttemptPhases = previousAttemptPhases
        .slice(0, MAX_PHASES)
        .map((p) => cap(p, 64))
    }

    const event: PlaygroundBootEvent = {
      eventType: 'playground_boot',
      bootId: buffer.bootId,
      startedAt,
      totalMs: Math.min(Math.max(0, endEpoch - startedAt), MAX_TOTAL_MS),
      outcome,
      phases,
      meta,
    }
    if (errorMessage) {
      event.errorMessage = cap(errorMessage, MAX_ERROR_MESSAGE_LENGTH)
    }
    return event
  }

  /**
   * Fire-and-forget flush. NEVER throws, NEVER blocks boot — a single
   * console.warn at most on failure. The terminal outcome is recorded on the
   * buffer BEFORE delivery: ready/error are terminal, so if the POST fails no
   * same-page retry runs — without this, the next load would mislabel the
   * FINISHED boot as abandoned. The next boot re-emits it with its recorded
   * outcome instead (at-least-once; downstream can dedupe by boot_id+outcome).
   * After a successful terminal flush the persisted buffer is cleared so the
   * next page load starts a fresh boot.
   */
  async flush(outcome: BootOutcome, errorMessage?: string): Promise<void> {
    if (this.flushedOutcome) return
    if (outcome !== 'abandoned') {
      this.buffer.terminalOutcome = outcome
      if (errorMessage) {
        this.buffer.terminalErrorMessage = cap(errorMessage, MAX_ERROR_MESSAGE_LENGTH)
      }
      this.persist()
    }
    this.flushedOutcome = outcome
    // Consume the held prior-boot attempts up front: they ride ON the buffer
    // (so they survived any mid-boot reload), and a later retry of THIS flush
    // must not re-emit them.
    const heldAttempts = this.buffer.heldAttempts ?? []
    this.buffer.heldAttempts = undefined
    let delivered = false
    try {
      const event = this.buildEvent(outcome, errorMessage)
      await this.postEvent(event)
      delivered = true
      // The current boot is delivered — clear its buffer BEFORE the best-effort
      // re-emissions, so a hang there can't leave the delivered boot's buffer
      // in storage to be re-emitted (as a phantom abandoned) on the next load.
      this.clearBuffer()
    } catch {
      this.flushedOutcome = null // allow a later terminal state to retry
      this.warn('[RUN:playground] Boot telemetry flush failed (non-fatal)')
    }
    // Held attempts are independent of the current boot's delivery — attempt
    // them even when the current boot's own POST failed (best-effort).
    const undelivered = await this.reemitHeldAttempts(heldAttempts)
    if (!delivered) {
      // This finished boot stays in storage for the next page's re-emission.
      // Put back ONLY the held boots we couldn't deliver, so the next page
      // re-holds exactly those (delivered ones aren't duplicated) — and
      // persist now, because the finished buffer is immutable (mark/time are
      // no-ops on it), so nothing else will.
      this.buffer.heldAttempts = undelivered.length > 0 ? undelivered : undefined
      this.persist()
    } else if (undelivered.length > 0) {
      // The current boot was delivered (buffer already cleared), but some
      // held boots were not. Keep exactly those in storage — riding on the
      // delivered buffer — so the next page re-emits them. The delivered
      // boot itself is re-emitted once more as a duplicate: at-least-once,
      // downstream dedupes by boot_id+outcome. persist() early-returns on a
      // flushed boot, so write storage directly.
      this.buffer.heldAttempts = undelivered
      try {
        this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.buffer))
      } catch {
        // storage full/blocked — telemetry is best-effort
      }
    }
  }

  /**
   * Re-emit held prior-boot attempts (abandoned / finished-but-undelivered).
   * Best-effort: individual failures never affect the current boot. Returns
   * the attempts that could NOT be delivered so the caller can keep them.
   */
  private async reemitHeldAttempts(heldAttempts: BootBuffer[]): Promise<BootBuffer[]> {
    const undelivered: BootBuffer[] = []
    for (const held of heldAttempts) {
      // endEpoch = the held buffer's own last activity, not the current clock.
      // A buffer with terminalOutcome is a FINISHED boot whose delivery
      // failed — re-emit with its recorded outcome, never as 'abandoned'.
      // Reload meta (persisted at merge time) rides along either way.
      const event = this.buildEventFrom(
        held,
        held.reloaded === true,
        held.previousAttemptPhases,
        held.terminalOutcome ?? 'abandoned',
        BootTelemetry.lastActivityEpoch(held),
        held.terminalErrorMessage,
      )
      try {
        await this.postEvent(event)
      } catch {
        undelivered.push(held)
      }
    }
    return undelivered
  }

  // ─── Internal ─────────────────────────────────────────────────────────────

  private loadBuffer(): BootBuffer | null {
    if (!this.storage) return null
    try {
      const raw = this.storage.getItem(STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw) as Partial<BootBuffer>
      const buffer = parseStoredBuffer(parsed)
      if (!buffer) return null
      // Held attempts are stored flat (never nested); parse each the same way.
      if (Array.isArray(parsed.heldAttempts)) {
        const held = parsed.heldAttempts
          .map((h) => parseStoredBuffer(h as Partial<BootBuffer>))
          .filter((h): h is BootBuffer => h !== null)
        if (held.length > 0) buffer.heldAttempts = held.slice(-MAX_HELD_ATTEMPTS)
      }
      return buffer
    } catch {
      return null
    }
  }

  private persist(): void {
    // After a terminal flush the buffer is cleared — late marks must not
    // re-persist it. (mark/time are also no-ops once terminalOutcome is set,
    // so a FINISHED buffer stays immutable for exact retry/re-emission; flush
    // itself persists explicitly on the delivery-failed path.)
    if (!this.storage || this.flushedOutcome) return
    try {
      this.storage.setItem(STORAGE_KEY, JSON.stringify(this.buffer))
    } catch {
      // storage full/blocked — telemetry is best-effort
    }
  }

  private clearBuffer(): void {
    try {
      this.storage?.removeItem(STORAGE_KEY)
    } catch {
      // best-effort
    }
  }

  /**
   * Adopt the toolbar script's start stamp and merge its observed marks.
   * Runs on every mark()/time() so toolbar marks are progressively persisted
   * into sessionStorage — otherwise they only existed as window globals and
   * were lost across the game-create / OAuth-redirect / manual reloads this
   * telemetry must survive. Idempotent per page (mergedToolbarPhases);
   * same-page dupes of SDK marks are dropped, but marks re-issued by a
   * post-reload page (preExistingPhases) are kept under a `:post-reload`
   * suffix so the page-two segment stays measurable. Script ordering between
   * the head-injected SDK init module and the body-injected toolbar script is
   * not guaranteed — either may eval first.
   */
  private mergeToolbarStamps(): void {
    if (typeof window === 'undefined') return
    try {
      const startAt = window.__RUNDOT_PLAYGROUND_BOOT_START__
      if (typeof startAt === 'number' && Number.isFinite(startAt)) {
        const epoch = performanceAtToEpoch(startAt)
        // Adopt only an EARLIER, plausible start: not future-dated (clock
        // skew between contexts) and within the last hour.
        if (
          epoch !== null &&
          epoch < this.buffer.startedAtEpoch &&
          epoch <= this.nowEpoch() &&
          this.nowEpoch() - epoch < 60 * 60 * 1000
        ) {
          this.buffer.startedAtEpoch = epoch
        }
      }

      const toolbarMarks = window.__RUNDOT_PLAYGROUND_BOOT_MARKS__
      if (Array.isArray(toolbarMarks)) {
        const bufferPhases = new Set(this.buffer.marks.map((m) => m.phase))
        for (const mark of toolbarMarks) {
          if (!mark || typeof mark.phase !== 'string' || typeof mark.at !== 'number') continue
          // Already merged from THIS page's globals (merge runs on every mark).
          if (this.mergedToolbarPhases.has(mark.phase)) continue
          const epoch = performanceAtToEpoch(mark.at)
          if (epoch === null) continue
          this.mergedToolbarPhases.add(mark.phase)
          if (this.preExistingPhases.has(mark.phase)) {
            // state:* is recorded authoritatively by the SDK's state machine
            // on every page — the toolbar's copy is always a dupe, drop it.
            if (mark.phase.startsWith('state:')) continue
            // The post-reload page re-issued this toolbar-specific stamp (e.g.
            // a second toolbar-script-start after game-create/OAuth). Keep it
            // under a distinct name — dropping it would lose the page-two
            // segment. Chained reloads (OAuth redirect → game-create) bump
            // the suffix rather than duplicating the name already in the buffer.
            let name = `${mark.phase}:post-reload`
            for (let n = 2; bufferPhases.has(name); n += 1) {
              name = `${mark.phase}:post-reload:${n}`
            }
            bufferPhases.add(name)
            this.buffer.marks.push({ phase: name, epoch })
          } else if (!bufferPhases.has(mark.phase)) {
            // New mark this page (same-page dupes of SDK marks are dropped).
            bufferPhases.add(mark.phase)
            this.buffer.marks.push({ phase: mark.phase, epoch })
          }
        }
      }
    } catch {
      // window globals are best-effort
    }
  }
}

// ─── Default transport ──────────────────────────────────────────────────────

/**
 * Authenticated POST to cloud-run through the same base-URL + Bearer-token
 * path as the other playground HTTP calls (`playgroundGameSession`,
 * `callCloudRunProfile`). Dynamic import avoids a module cycle with
 * firebaseClient (which itself imports this module). keepalive lets the
 * request outlive page teardown when the flush races a reload.
 */
async function defaultPostEvent(event: PlaygroundBootEvent): Promise<void> {
  const { getFirebaseClient } = await import('../firebase/firebaseClient')
  const client = await getFirebaseClient()
  const idToken = await client.getIdTokenOrThrow()
  const response = await fetch(`${getCloudRunUrl()}/v1/playground-telemetry`, {
    method: 'POST',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(event),
  })
  if (!response.ok) {
    throw new Error(`boot telemetry POST failed (${response.status})`)
  }
}

function defaultSdkVersion(): string {
  try {
    if (typeof SDK_VERSION === 'string' && SDK_VERSION) return SDK_VERSION
  } catch {
    // __SDK_VERSION__ not injected (raw ts execution)
  }
  return getPlaygroundConfig()?.sdkVersion ?? 'unknown'
}

// ─── Singleton + ergonomic helpers ──────────────────────────────────────────

let instance: BootTelemetry | null = null

/**
 * Construct the singleton. Playground-gated: returns null (and stays null)
 * outside playground mode so non-playground games are untouched. Never throws
 * — telemetry must not be able to break boot-critical callers.
 */
export function initBootTelemetry(): BootTelemetry | null {
  try {
    if (instance) return instance
    if (typeof window === 'undefined' || !isPlaygroundEnabled()) return null
    instance = new BootTelemetry()
    return instance
  } catch {
    return null
  }
}

export function getBootTelemetry(): BootTelemetry | null {
  return instance ?? initBootTelemetry()
}

export function markBootPhase(phase: string, durationMs?: number): void {
  if (durationMs === undefined) {
    getBootTelemetry()?.mark(phase)
  } else {
    getBootTelemetry()?.mark(phase, durationMs)
  }
}

export async function timeBootPhase<T>(phase: string, fn: () => Promise<T>): Promise<T> {
  const telemetry = getBootTelemetry()
  if (!telemetry) return fn()
  return telemetry.time(phase, fn)
}

export function setBootAuthPath(authPath: BootAuthPath): void {
  getBootTelemetry()?.setAuthPath(authPath)
}

export function setBootSlot(slot: number | null): void {
  getBootTelemetry()?.setSlot(slot)
}

/** Call immediately before the deliberate mid-boot page reload (game-create). */
export function expectBootReload(): void {
  getBootTelemetry()?.expectReload()
}

/** Undo expectBootReload() when the anticipated navigation never happened. */
export function cancelBootReload(): void {
  getBootTelemetry()?.cancelExpectReload()
}

/** Fire-and-forget flush on a terminal state; safe to call from boot-critical code. */
export function flushBootTelemetry(outcome: 'ready' | 'error', errorMessage?: string): void {
  const telemetry = getBootTelemetry()
  if (!telemetry) return
  void telemetry.flush(outcome, errorMessage)
}

/** Test hook: drop the singleton so each test can construct a fresh one. */
export function resetBootTelemetryForTests(): void {
  instance = null
}
